<template>
  <v-card>
    <v-toolbar color="#1b2643" dark>
      {{ $t('editPackage.editDialogHeader') }}
    </v-toolbar>
    <v-card-text style="padding: 0; overflow-x: scroll; max-height: 500px">
      <v-expansion-panels :value="[0, 1, 2]" accordion multiple>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-h6">{{ $t('editPackage.packageSettings') }}</span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <table class="dialog-table">
              <tr>
                <td>{{ $t('editPackage.packageCodeLabel') }}</td>
                <td>{{ packageCode }}</td>
              </tr>
              <tr>
                <td>{{ $t('editPackage.packageNameLabel') }}</td>
                <td>
                  <span
                    :style="{
                      color: modifications?.packageName ? 'red' : 'inherit',
                    }"
                  >
                    {{ packageName }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>{{ $t('editPackage.viewingPeriodLabel') }}</td>
                <td>
                  <span
                    :style="{
                      color: isViewingPeriodFieldModified ? 'red' : 'inherit',
                    }"
                  >
                    {{ viewingPeriodDateTime }}
                  </span>
                </td>
              </tr>
            </table>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-h6">
              {{
                $t('editPackage.campaigns.title', {
                  count: editableCampaigns.length,
                })
              }}
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="d-flex flex-column" style="gap: 20px">
              <div v-for="(campaign, index) in editableCampaigns" :key="index">
                <p class="text-body-1 font-weight-bold">
                  <u>{{
                    $t('editPackage.campaigns.index', { index: index + 1 })
                  }}</u>
                </p>
                <table class="dialog-table dialog-campaign-table">
                  <tr>
                    <td>{{ $t('editPackage.campaigns.name') }}</td>
                    <td>
                      <span
                        :style="{
                          color: isCampaignFieldModified?.(index, 'name')
                            ? 'red'
                            : 'inherit',
                        }"
                      >
                        {{ campaign.name }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('editPackage.campaigns.scenario') }}</td>
                    <td>
                      <span
                        :style="{
                          color: isCampaignFieldModified?.(
                            index,
                            'scenarioDocId'
                          )
                            ? 'red'
                            : 'inherit',
                        }"
                      >
                        {{ getScenarioNameById(campaign.scenarioDocId) }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('editPackage.campaigns.interviewCount') }}</td>
                    <td>
                      <span
                        :style="{
                          color: isCampaignFieldModified?.(index, 'limits')
                            ? 'red'
                            : 'inherit',
                        }"
                      >
                        {{
                          campaign.limits === null
                            ? $t('newPackage.campaign.unlimited')
                            : campaign.limits ?? 1
                        }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('editPackage.campaigns.interviewPeriod') }}</td>
                    <td>
                      <span
                        :style="{
                          color: isCampaignInterviewPeriodModified?.(campaign)
                            ? 'red'
                            : 'inherit',
                        }"
                      >
                        {{ interviewDateTime?.(campaign) }}
                      </span>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-h6">
              {{ $t('editPackage.addNewCoupons.title') }}
              <span style="color: red"
                >({{ importedCoupons?.length || 0 }})</span
              >
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-data-table
              :headers="couponHeaders"
              :items="importedCoupons"
              :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
              dense
            ></v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
    <v-alert
      v-if="errorMessage"
      type="error"
      class="mx-4 mt-4"
      dismissible
      @input="$emit('clearError')"
    >
      {{ errorMessage }}
    </v-alert>
    <v-card-actions class="justify-end pt-4 pb-4">
      <v-btn text @click="$emit('cancel')">
        <v-icon left>mdi-arrow-u-left-top</v-icon>
        {{ $t('editPackage.cancel') }}
      </v-btn>
      <v-btn :loading="isEditing" color="#1b2643" dark @click="$emit('submit')">
        <v-icon left>mdi-check</v-icon>
        {{ $t('editPackage.edit') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { CampaignField } from "@/resources/campaign";
export default defineComponent({
  name: "EditPackageDialog",
  props: {
    packageCode: {
      type: String,
      required: true,
    },
    packageName: {
      type: String,
      required: true,
    },
    modifications: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({}),
    },
    isViewingPeriodFieldModified: {
      type: Boolean,
      default: false,
    },
    viewingPeriodDateTime: {
      type: String,
      default: "",
    },
    editableCampaigns: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    isCampaignFieldModified: {
      type: Function as PropType<
        (index: number, field: CampaignField) => boolean
      >,
      default: () => () => false,
    },
    getScenarioNameById: {
      type: Function as PropType<(id: string) => string>,
      default: () => () => "",
    },
    isCampaignInterviewPeriodModified: {
      type: Function as PropType<(campaign: any) => boolean>,
      default: () => () => false,
    },
    interviewDateTime: {
      type: Function as PropType<(campaign: any) => string>,
      default: () => () => "",
    },
    importedCoupons: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    couponHeaders: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    itemsPerPageOptions: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "",
    },
  },
});
</script>
